import { c as _c } from "react/compiler-runtime";
import type { ApiOnboardingGraph } from '@onefootprint/request-types/dashboard';
import { type Edge, type Node, useEdgesState, useNodesInitialized, useNodesState, useReactFlow } from '@xyflow/react';
import { useEffect } from 'react';
import { reactFlowNodeType } from '../playbook-builder.types';
import getCollapseResult from '../utils/collapse-branches';
import getBoKycElementsWithLayout from '../utils/get-bo-kyc-elements-with-layout';
import getElementsWithLayout from '../utils/get-elements-with-layout';
import type { InitialEdgeData, InitialNodeData } from '../utils/parse-graph';
import { parseGraph } from '../utils/parse-graph';
import { recalculateJoinPointMinlens } from '../utils/recalculate-join-point-minlens';
import useStore from './use-store';
const useLayout = () => {
  const $ = _c(21);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [nodes, setNodes, onNodesChange] = useNodesState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [edges, setEdges, onEdgesChange] = useEdgesState(t1);
  const reactFlow = useReactFlow();
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isInitialized = useNodesInitialized(t2);
  const {
    data: storeData
  } = useStore();
  let t3;
  if ($[3] !== isInitialized || $[4] !== reactFlow || $[5] !== setEdges || $[6] !== setNodes) {
    t3 = (graph, collapsedNodeIds) => {
      const {
        nodes: nodesFromData,
        edges: edgesFromData
      } = parseGraph(graph);
      const allParsedEdges = [...edgesFromData.rootEdges, ...edgesFromData.boKycEdges];
      const {
        hiddenIds,
        bridgeEdges
      } = getCollapseResult(allParsedEdges, graph, collapsedNodeIds);
      const splitVisible = items => ({
        visible: items.filter(n => !hiddenIds.has(n.id)),
        hidden: items.filter(n_0 => hiddenIds.has(n_0.id))
      });
      const filterEdges = edges_0 => edges_0.filter(e => !hiddenIds.has(e.source) && !hiddenIds.has(e.target));
      const rootNodesSplit = splitVisible(nodesFromData.rootNodes);
      const boKycNodesSplit = splitVisible(nodesFromData.boKycChildNodes);
      const boKycChildNodeIds = new Set(nodesFromData.boKycChildNodes.map(_temp));
      const rootBridgeEdges = bridgeEdges.filter(e_0 => !boKycChildNodeIds.has(e_0.source));
      const boKycBridgeEdges = bridgeEdges.filter(e_1 => boKycChildNodeIds.has(e_1.source));
      const visibleRootNodeIds = new Set(rootNodesSplit.visible.map(_temp2));
      const visibleRootEdges = recalculateJoinPointMinlens([...filterEdges(edgesFromData.rootEdges), ...rootBridgeEdges], graph, visibleRootNodeIds);
      const visibleBoKycNodeIds = new Set(boKycNodesSplit.visible.map(_temp3));
      const visibleBoKycEdges = recalculateJoinPointMinlens([...filterEdges(edgesFromData.boKycEdges), ...boKycBridgeEdges], graph, visibleBoKycNodeIds);
      const getLayoutResult = () => {
        const hasBoKyc = boKycNodesSplit.visible.length > 0 && visibleBoKycEdges.length > 0;
        const boKycNodeIsVisible = rootNodesSplit.visible.some(_temp4);
        if (hasBoKyc && boKycNodeIsVisible) {
          return getBoKycElementsWithLayout(rootNodesSplit.visible, boKycNodesSplit.visible, visibleRootEdges, visibleBoKycEdges);
        }
        return getElementsWithLayout(rootNodesSplit.visible, visibleRootEdges);
      };
      const {
        nodes: layoutedNodes,
        edges: layoutedEdges,
        clientCenter
      } = getLayoutResult();
      const hiddenNodesOutput = [...rootNodesSplit.hidden, ...boKycNodesSplit.hidden].map(_temp5);
      const hiddenEdgesOutput = allParsedEdges.filter(e_2 => hiddenIds.has(e_2.source) || hiddenIds.has(e_2.target)).map(_temp6);
      const finalNodes = [...layoutedNodes, ...hiddenNodesOutput];
      const finalEdges = [...layoutedEdges, ...hiddenEdgesOutput];
      if (isInitialized) {
        setNodes(finalNodes);
        setEdges(finalEdges);
      } else {
        setTimeout(() => {
          setNodes(finalNodes);
          setEdges(finalEdges);
          reactFlow.setCenter(clientCenter.x, clientCenter.y, {
            zoom: clientCenter.zoom
          });
        }, 100);
      }
    };
    $[3] = isInitialized;
    $[4] = reactFlow;
    $[5] = setEdges;
    $[6] = setNodes;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const refreshLayout = t3;
  let t4;
  if ($[8] !== refreshLayout || $[9] !== storeData.collapsedNodeIds || $[10] !== storeData.graph) {
    t4 = () => {
      refreshLayout(storeData.graph, storeData.collapsedNodeIds);
    };
    $[8] = refreshLayout;
    $[9] = storeData.collapsedNodeIds;
    $[10] = storeData.graph;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== storeData.collapsedNodeIds || $[13] !== storeData.graph) {
    t5 = [storeData.graph, storeData.collapsedNodeIds];
    $[12] = storeData.collapsedNodeIds;
    $[13] = storeData.graph;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  if ($[15] !== edges || $[16] !== isInitialized || $[17] !== nodes || $[18] !== onEdgesChange || $[19] !== onNodesChange) {
    t6 = {
      nodes,
      edges,
      onNodesChange,
      onEdgesChange,
      isInitialized
    };
    $[15] = edges;
    $[16] = isInitialized;
    $[17] = nodes;
    $[18] = onEdgesChange;
    $[19] = onNodesChange;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
};
export default useLayout;
function _temp(n_1) {
  return n_1.id;
}
function _temp2(n_2) {
  return n_2.id;
}
function _temp3(n_3) {
  return n_3.id;
}
function _temp4(n_4) {
  return n_4.type === reactFlowNodeType("bo_kyc");
}
function _temp5(n_5) {
  return {
    ...n_5,
    hidden: true as const
  };
}
function _temp6(e_3) {
  return {
    ...e_3,
    hidden: true as const
  };
}